import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Shared FooterBar — fixed bottom bar with pagination + Loombiz logo.
 *
 * Props:
 *   totalPages, currentPage (1-based), onPageChange, totalItems, pageSize
 *   — if totalPages <= 1, only logo is shown
 */
export default function FooterBar({
  totalPages = 0,
  currentPage = 1,
  onPageChange,
  totalItems = 0,
  pageSize = 20,
}) {
  const showPagination = totalPages > 1;

  const maxButtons = 5;
  let startPage = Math.max(currentPage - Math.floor(maxButtons / 2), 1);
  let endPage = startPage + maxButtons - 1;
  if (endPage > totalPages) { endPage = totalPages; startPage = Math.max(endPage - maxButtons + 1, 1); }
  const pages = [];
  for (let i = startPage; i <= endPage; i++) pages.push(i);

  const startItem = totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const btnCls = "p-1 rounded text-[#888] hover:bg-[#FFF4E6] hover:text-[#C45500] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#888] transition-colors";
  const numCls = (active) => `min-w-[28px] h-7 px-2 rounded text-[12px] font-semibold transition-all ${active ? 'bg-[#232F3E] text-white' : 'text-[#565959] hover:bg-[#FFF4E6] hover:text-[#C45500]'}`;

  return (
    <div className="bg-white border-t border-[#D5D9D9] px-3 sm:px-4 py-1.5 flex items-center justify-between sticky bottom-0 z-10">
      {/* Left: Logo */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <img src="/loombiz-logo.png" alt="Loombiz" className="h-5 sm:h-6 object-contain" />
        <span className="text-[9px] text-[#999] hidden sm:inline">v1.0</span>
      </div>

      {/* Center: Page info */}
      {showPagination && totalItems > 0 && (
        <span className="text-[11px] text-[#888] tabular-nums hidden sm:block">
          {startItem}–{endItem} of {totalItems}
        </span>
      )}

      {/* Right: Pagination */}
      {showPagination && (
        <nav className="flex items-center gap-0.5">
          <button onClick={() => onPageChange(1)} disabled={currentPage === 1} className={btnCls} title="First">
            <ChevronsLeft size={14} />
          </button>
          <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className={btnCls} title="Previous">
            <ChevronLeft size={14} />
          </button>

          {startPage > 1 && (
            <>
              <button onClick={() => onPageChange(1)} className={numCls(false)}>1</button>
              {startPage > 2 && <span className="text-[10px] text-[#999] px-0.5">...</span>}
            </>
          )}

          {pages.map(p => (
            <button key={p} onClick={() => onPageChange(p)} className={numCls(p === currentPage)}>{p}</button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="text-[10px] text-[#999] px-0.5">...</span>}
              <button onClick={() => onPageChange(totalPages)} className={numCls(false)}>{totalPages}</button>
            </>
          )}

          <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} className={btnCls} title="Next">
            <ChevronRight size={14} />
          </button>
          <button onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages} className={btnCls} title="Last">
            <ChevronsRight size={14} />
          </button>
        </nav>
      )}

      {/* If no pagination, empty spacer for alignment */}
      {!showPagination && <div />}
    </div>
  );
}
